import { useTranslation } from 'react-i18next';
import { Box, Button, Spinner, Text } from 'grommet';
import { Step } from '../../../types/Step';
import { showToast } from '../../../service/common/utils';
import { useEffect, useState } from 'react';
import { TFunction } from 'i18next';
import { scrollToError } from '../WizardHelper';
import SyncFieldsModal from '../../Modals/SyncFieldsModal';
import SyncErrorModal from '../../Modals/SyncErrorModal/SyncErrorModal';
import EstimateErrorModal from '../../Modals/EstimateErrorModal';
import { EnumProcessingStatus } from '../../../service/common/types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import FieldProcessingModal from '../../Modals/FieldProcessingModal';
import FinalizeIneligibleAreasModal from '../../Modals/FinalizeIneligibleAreasModal';
import { useDispatch } from 'react-redux';
import { getFields, getSyncAvailability, processIneligibleAreas, saveFields } from '../../../../../redux/actions/map-actions/field/field-action';
import { getSyncAvailabilityEstimate, upsertShapefile } from '../../../../../service/map/map.service';
import { StyledTooltip } from '../../../../../components/common/StyledTooltip';
import { useAppSelector } from '../../../../../redux';

interface NextStepButtonProps {
  steps: Step[];
  currentStep: Step;
  stepIndex: number;
  showLoading: boolean;
  setStepIndex: (value: ((prevState: number) => number) | number) => void;
  setShowDateRangeModal: (value: ((prevState: boolean) => boolean) | boolean) => void;
  setShowLoading: (value: ((prevState: boolean) => boolean) | boolean) => void;
}

const NextStepButton = ({ currentStep, stepIndex, showLoading, setShowLoading }: NextStepButtonProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { enableAsyncFieldProcessing, upsellingBoundaryTool } = useFlags();

  const [showFinalizeModal, setShowFinalizeModal] = useState<boolean>(false);
  const [showSyncErrorModal, setShowSyncErrorModal] = useState<boolean>(false);
  const [showEstimatesModal, setShowEstimatesModal] = useState<boolean>(false);
  const [showFieldProcessingModal, setShowFieldProcessingModal] = useState<boolean>(false);
  const [showFinalizeIneligibleAreasModal, setShowFinalizeIneligibleAreasModal] = useState<boolean>(false);
  const [loadingSync, setLoadingSync] = useState<boolean>(false);

  // const saveFunctions = useRef<(() => Promise<void>)[]>([]);

  const country = useAppSelector((state) => state.producer.producerInformation?.country ?? 'us');
  const producerData = useAppSelector((state) => state.producer.producerInformation);
  const journey = useAppSelector((state) => state.producer.journeyInformation);
  const enableFinalize = useAppSelector((state) => state.map.enableFinalize);
  const fields = useAppSelector((state) => state.map.fields);
  const deletedFields = useAppSelector((state) => state.map.deletedFields);
  const updatedFieldBoundaries = useAppSelector((state) => state.map.updatedFieldBoundaries);
  const contractPaymentOption = useAppSelector((state) => state.map.contractPaymentOption);
  const syncErrors = useAppSelector((state) => state.map.syncErrors);
  const estimate = useAppSelector((state) => state.map.estimate);
  const contractVersion = useAppSelector((state) => state.map.contractVersion);
  const contractInSF = useAppSelector((state) => state.map.contractInSF);
  const isNewContract = Boolean((!contractVersion && !contractInSF) || contractVersion?.estimateVersion?.startsWith('agoro-'));
  const invalidField = useAppSelector((state) => {
    if (enableAsyncFieldProcessing && country === 'us' && !state.map.ineligibleAreasFinalized) {
      return !state.map.fields.length;
    }
    return (
      state.map.fieldsDrawingInvalid ||
      !state.map.fields.length ||
      !state.map.overlap ||
      (state.map.overlap ?? []).length ||
      !state.map.enableFinalize
    );
  });
  const overlaps = useAppSelector((state) => state.map.overlap);
  const ineligibleAreasFinalized = useAppSelector((state) => state.map.ineligibleAreasFinalized);
  const processingStatus = useAppSelector((state) => state.map.processingStatus);
  const shapefileStatus = useAppSelector((state) => state.map.shapefileStatus);

  // function paddockStepForward() {
  //   dispatch(setNotSavedPaddockVersion());
  //   saveFunctions.current = [...saveFunctions.current, /*saveVersion*/];
  // }
  //
  // function herdStepForward(skip?: boolean) {
  //   if (!skip) {
  //     saveFunctions.current = [...saveFunctions.current, /*saveHerds*/];
  //   } else {
  //     // clearHerds();
  //   }
  // }

  // async function stepForward(skip?: boolean) {
  //   setShowLoading(true);
  //   const stepFunction: Record<number, () => Promise<any>> = {
  //     1: async () => paddockStepForward(),
  //     2: async () => herdStepForward(skip),
  //   };
  //
  //   await stepFunction[stepIndex]();
  //   if (stepIndex !== steps.length - 1) {
  //     setShowLoading(false);
  //     setStepIndex((stepIndex) => stepIndex + 1 + (steps[stepIndex + 1].ignoreStep ? 1 : 0));
  //   } else {
  //     for (const saveFunction of saveFunctions.current) {
  //       await saveFunction();
  //     }
  //     saveFunctions.current = [];
  //     setShowLoading(false);
  //     setWillLoseProgressOnExit(false);
  //     setState('OVERVIEW');
  //   }
  // }

  useEffect(() => {
    if (syncErrors.status === 'UNKNOWN') {
      return;
    }

    if (syncErrors.status !== 'OK' && syncErrors.missingPractices.length) {
      showToast('Erro', 'Por favor preencha todos os campos para poder finalizar os limites', 'error', 'payment-option-toast');
      scrollToError('practices-' + syncErrors.missingPractices[0]);
      return;
    }
    if (country === 'br') {
      const available = getSyncAvailabilityEstimate(fields, estimate, country);
      if (!available) {
        setShowEstimatesModal(true);
        return;
      }
    }
    // sync availability
    if (syncErrors.status === 'OK') {
      setShowFinalizeModal(true);
    }
    if (syncErrors.status !== 'OK' && syncErrors.paymentOptionErrors.length) {
      showToast('Error', 'Please fill the PAYMENT OPTION for all fields', 'error', 'payment-option-toast');
      scrollToError(`paymentOption${syncErrors.paymentOptionErrors[0]?.id}`);
    } else if (syncErrors.status !== 'OK' && syncErrors.practices.length) {
      setShowSyncErrorModal(true);
    }
    setLoadingSync(false);
  }, [syncErrors]);

  async function fieldNextStep(
    t: TFunction<'translation'>,
    setShowFieldProcessingModal: (bool) => void,
    setShowFinalizeIneligibleAreasModal: (bool) => void,
  ) {
    try {
      setLoadingSync(true);
      await dispatch(saveFields(journey, fields, deletedFields, updatedFieldBoundaries));

      if (enableAsyncFieldProcessing && country === 'us' && processingStatus === EnumProcessingStatus.NOT_PROCESSED) {
        setShowFieldProcessingModal(true);
        dispatch(
          processIneligibleAreas(journey.id, () => {
            setShowFieldProcessingModal(false);
            dispatch(getFields(journey.id));
            setLoadingSync(false);
          }),
        );
        return;
      }

      if (enableAsyncFieldProcessing && country === 'us' && processingStatus === EnumProcessingStatus.PROCESSED && !ineligibleAreasFinalized) {
        setShowFinalizeIneligibleAreasModal(true);
        setLoadingSync(false);
        return;
      }

      if (shapefileStatus !== 'GATHERING') await upsertShapefile(journey, 'GATHERING', country, contractPaymentOption);
      dispatch(getSyncAvailability(country, upsellingBoundaryTool && !!producerData?.caseStatusHistory?.find((i) => i.status === 'In Support')));
      setLoadingSync(false);
    } catch {
      showToast(t('fieldFlow.errors.error'), t('fieldFlow.errors.syncSetup'), 'error', 'sync-setup-error');
      setLoadingSync(false);
    }
  }

  function handleNextStep() {
    // TODO always considering fields step since grazing is disabled
    // if (stepIndex === 0) {
    fieldNextStep(t, setShowFieldProcessingModal, setShowFinalizeIneligibleAreasModal);
    // } else {
    //   stepForward();
    // }
  }

  function getToolTipLabel() {
    if (!enableFinalize && country === 'br') {
      return (
        <>
          <Text>Estimativa não está conectada</Text>
          <Text>a um contrato. Por favor entre</Text>
          <Text>em contato com a equipe de produto</Text>
        </>
      );
    } else {
      if (isNewContract) {
        return <Text>{t('fieldFlow.mainWizard.Fields.disabledSyncByContractVersion')}</Text>;
      } else if (!overlaps?.length) {
        return (
          <>
            <Text>{t('fieldFlow.mainWizard.Fields.cannotSyncLead.leadsCannot')}</Text>
            <Text>{t('fieldFlow.mainWizard.Fields.cannotSyncLead.syncedBoundaries')}</Text>
            <Text>{t('fieldFlow.mainWizard.Fields.cannotSyncLead.contactAdmin')}</Text>
          </>
        );
      }
      return (
        <>
          <Text>{t('fieldFlow.mainWizard.Fields.cannotSyncOverlap.youCantSynchronize')}</Text>
          <Text>{t('fieldFlow.mainWizard.Fields.cannotSyncOverlap.theFieldsWhen')}</Text>
          <Text>{t('fieldFlow.mainWizard.Fields.cannotSyncOverlap.overlap')}</Text>
        </>
      );
    }
  }

  function showTooltip() {
    const syncLogic = !producerData?.opportunityId || !!overlaps?.length || !enableFinalize || isNewContract;
    if (enableAsyncFieldProcessing && country === 'us') {
      return ineligibleAreasFinalized && syncLogic;
    }
    return syncLogic;
  }

  if (currentStep?.hideButton) {
    return null;
  }

  return (
    <>
      {showEstimatesModal && <EstimateErrorModal setShowEstimatesModal={setShowEstimatesModal} />}
      {showSyncErrorModal && (
        <SyncErrorModal
          cancel={() => {
            setShowSyncErrorModal(false);
          }}
        />
      )}
      {showFinalizeModal && <SyncFieldsModal setShowFinalizeModal={setShowFinalizeModal} setShowLoading={setShowLoading} />}
      {showFieldProcessingModal && <FieldProcessingModal />}
      {showFinalizeIneligibleAreasModal && <FinalizeIneligibleAreasModal closeModal={() => setShowFinalizeIneligibleAreasModal(false)} />}
      <Box direction={'row'} gap={'1rem'} align={'center'}>
        {/*{stepIndex > 1 && state !== 'SECOND_SETUP' ? (*/}
        {/*  <Box width={'111px'} onClick={() => stepForward(true)}>*/}
        {/*    <Text color={'#1457A8'} weight={400}>*/}
        {/*      {t('fieldFlow.mainWizard.skip')}*/}
        {/*    </Text>*/}
        {/*  </Box>*/}
        {/*) : null}*/}
        <StyledTooltip
          dropOptions={enableFinalize ? 'left' : 'top'}
          label={<Box data-cy={'sync-error-hover-message'}>{getToolTipLabel()}</Box>}
          showToolTip={showTooltip()}>
          <Button
            data-cy={`confirm${currentStep.name}-button`}
            as={'button'}
            //Grommet Button with disable adds pointerEvent: 'none', breaking the tooltip
            onClick={() => {
              if (invalidField || showLoading || isNewContract) {
                return;
              }
              handleNextStep();
            }}
            justify={'center'}
            style={{
              alignItems: 'center',
              minWidth: '140px',
              textAlign: 'center',
              border: 0,
              fontWeight: 'unset',
              padding: '1rem 1.5rem',
              opacity: invalidField || isNewContract ? 0.7 : 1,
              background: stepIndex === 0 ? '#FFE137' : '#1B7C3D',
              color: stepIndex === 0 ? '#282828' : '#FFF',
            }}>
            {showLoading || loadingSync ? (
              <Box width={'100%'}>
                <Spinner alignSelf={'center'} width={'10px'} height={'10px'} color={'#282828'} />
              </Box>
            ) : (
              <Box width={'100%'} direction={'row'} gap={'8px'} justify={'center'}>
                <Text weight={700} textAlign={'center'} size={'1rem'} style={{ lineHeight: 'unset', whiteSpace: 'nowrap' }}>
                  {t(`fieldFlow.mainWizard.mainButton.${currentStep.name}`)}
                </Text>
              </Box>
            )}
          </Button>
        </StyledTooltip>
      </Box>
    </>
  );
};

export default NextStepButton;
